import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { PrimaryButton } from "./ui";
import "./Dialog.css";

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = true,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Enter") onConfirm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onConfirm, onClose]);

  if (!open) return null;

  // Portal to <body>: callers render this dialog from inside containers that
  // form their own stacking context (e.g. the Sidebar is `position: sticky`),
  // which would trap the `position: fixed` backdrop *below* the main content —
  // the dialog then appears behind the page and its buttons aren't clickable.
  // Rendering at the document root lets z-index:100 win globally.
  return createPortal(
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <div
        className="dialog"
        role="alertdialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="dialog__head">
          <h2>{title}</h2>
          <button type="button" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </header>
        {body ? <div className="dialog__body">{body}</div> : null}
        <div className="dialog__actions" style={{ padding: "0 20px 20px" }}>
          <button type="button" className="btn--outline" onClick={onClose}>
            {cancelLabel}
          </button>
          <PrimaryButton
            type="button"
            className={`btn--auto${destructive ? " btn--danger" : ""}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </PrimaryButton>
        </div>
      </div>
    </div>,
    document.body,
  );
}
